// ---------------------------------------------------------------------------
// React Query hook factories
//
// Removes the repeated boilerplate in entities/*/api.ts:
//   - `createMutationHook` — `useQueryClient` + `useMutation` + invalidate
//   - `createListQuery`    — `useQuery` over a react-admin sort/range/filter list
//
// Only the two regular patterns are factored. Hooks with bespoke signatures
// (positional extra args), multi-step queryFns, closure-scoped invalidation or
// simple non-list queries stay hand-written — a factory there hurts clarity.
// ---------------------------------------------------------------------------

import {
    useMutation,
    useQuery,
    useQueryClient,
    keepPreviousData,
    type QueryClient,
    type QueryKey,
    type UseMutationResult,
    type UseQueryResult,
} from "@tanstack/react-query";
import { raParams, type RASort, type RARange, type RAFilter, type RAParamValue } from "./ra-params";

// ---------------------------------------------------------------------------
// Mutation
// ---------------------------------------------------------------------------

interface CreateMutationHookConfig<TData, TVars> {
    mutationFn: (vars: TVars) => Promise<TData>;
    /**
     * Query keys to invalidate on success. Either a static list, or a function
     * of `(data, vars)` for variable-derived keys
     * (e.g. `(_d, vars) => [workspaceKeys.balances(vars.workspaceId)]`).
     */
    invalidate?: QueryKey[] | ((data: TData, vars: TVars) => QueryKey[]);
    /**
     * Optional extra side-effect, runs BEFORE invalidation. Receives the
     * QueryClient so `setQueryData` cases work without losing the default
     * invalidation.
     */
    onSuccess?: (data: TData, vars: TVars, qc: QueryClient) => void;
}

/**
 * Build a mutation hook that invalidates query keys on success.
 *
 * Returns a hook with the canonical zero-argument signature
 * `() => UseMutationResult<TData, Error, TVars>`, matching the existing
 * `export function useX()` mutation hooks 1-to-1.
 */
export function createMutationHook<TData, TVars = void>(
    config: CreateMutationHookConfig<TData, TVars>,
): () => UseMutationResult<TData, Error, TVars> {
    return function useGeneratedMutation() {
        const qc = useQueryClient();
        return useMutation<TData, Error, TVars>({
            mutationFn: config.mutationFn,
            onSuccess: (data, vars) => {
                config.onSuccess?.(data, vars, qc);
                const keys =
                    typeof config.invalidate === "function"
                        ? config.invalidate(data, vars)
                        : (config.invalidate ?? []);
                for (const key of keys) {
                    void qc.invalidateQueries({ queryKey: key });
                }
            },
        });
    };
}

// ---------------------------------------------------------------------------
// List query (react-admin sort/range/filter, with total)
// ---------------------------------------------------------------------------

interface ListResult<T> {
    data: T;
    total: number;
}

interface CreateListQueryConfig<TItem> {
    baseKey: QueryKey;
    path: string;
    /**
     * Transport that resolves to `{ data, total }`. Inject `apiGetWithTotal` /
     * `billingGetWithTotal` directly, or wrap them when the endpoint returns a
     * bespoke body shape (e.g. `{ users, total }`).
     */
    fetcher: (path: string, params: Record<string, RAParamValue>) => Promise<ListResult<TItem[]>>;
    /** Pass `keepPreviousData` as `placeholderData` (reduces pagination flicker). */
    keepPrevious?: boolean;
}

/**
 * Build a paginated list-query hook with the canonical signature
 * `(sort?, range?, filter?, opts?) => UseQueryResult<{ data; total }, Error>`.
 */
export function createListQuery<TItem>(config: CreateListQueryConfig<TItem>) {
    return function useGeneratedList(
        sort?: RASort,
        range?: RARange,
        filter?: RAFilter,
        opts?: { extra?: Record<string, RAParamValue>; enabled?: boolean },
    ): UseQueryResult<ListResult<TItem[]>, Error> {
        const queryKey: QueryKey =
            opts?.extra !== undefined
                ? [...config.baseKey, sort, range, filter, opts.extra]
                : [...config.baseKey, sort, range, filter];
        return useQuery<ListResult<TItem[]>, Error>({
            queryKey,
            queryFn: () => config.fetcher(config.path, raParams(sort, range, filter, opts?.extra)),
            placeholderData: config.keepPrevious ? keepPreviousData : undefined,
            enabled: opts?.enabled,
        });
    };
}
